import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, Button, CircularProgress, Divider, Tooltip, Typography } from "@mui/material";

import { getCandidateGinoFitScoreRoute } from "../../../../../../routers/routeUtils";

import { CandidateDetails } from "../../../../types/candidate";
import { FitScoreIcon, FitScoreInfoIcon } from "../../../../assets/icons/candidate-details-page-icons";

const CandidateFitScore = ({ candidateDetails }: { candidateDetails: CandidateDetails }) => {
  const { t } = useTranslation("goHire", { keyPrefix: "candidateDetailsPage.overviewTab" });

  const navigate = useNavigate();

  const handleSeeFullDetails = () =>
    navigate(getCandidateGinoFitScoreRoute(candidateDetails.user.id, candidateDetails.user.id));

  return (
    <Box sx={styles.fittingScoreContainer}>
      <Box sx={styles.fittingScoreInnerContainer}>
        <Box sx={styles.fitScoreMeter}>
          <Box sx={styles.fittingScoreTitleContainer}>
            <FitScoreIcon />
            <Typography sx={styles.fittingScoreTitle}>{t("fittingScore")}</Typography>
            <Tooltip title={t("fittingScore")}>
              <FitScoreInfoIcon />
            </Tooltip>
          </Box>
          <Box sx={styles.fittingScoreProgressContainer}>
            <CircularProgress
              sx={styles.trackSlot(candidateDetails.fitScore)}
              value={candidateDetails.fitScore / 2}
              size={240}
              thickness={4}
              variant="determinate"
            />
            <CircularProgress
              sx={styles.fittingScoreProgress}
              value={50}
              size={240}
              thickness={4}
              variant="determinate"
            />
            <Box sx={styles.fittingScoreMatchingContainer}>
              <Typography sx={styles.fittingScoreMatching}>
                {candidateDetails.fitScore > 60
                  ? t("highMatch")
                  : candidateDetails.fitScore > 30
                  ? t("mediumMatch")
                  : t("lowMatch")}
              </Typography>
              <Typography sx={styles.fittingScoreValue}>{candidateDetails.fitScore}%</Typography>
            </Box>
          </Box>
        </Box>
        <Divider flexItem orientation="vertical" />
        <Box sx={styles.fittingScoreJustificationContainer}>
          <Typography sx={styles.fittingScoreJustificationTitle}>{t("fitScoreJustification")}</Typography>
          {candidateDetails.fitScoreJustification && (
            <Typography sx={styles.fittingScoreJustificationValue}>{candidateDetails.fitScoreJustification}</Typography>
          )}
          {!candidateDetails.fitScoreJustification && (
            <Typography sx={styles.fittingScoreJustificationValueComingSoon}>{t("fitScoreComingSoon")}</Typography>
          )}
        </Box>
      </Box>
      <Divider flexItem orientation="horizontal" />
      {/* <Box sx={styles.seeFullDetailsButtonContainer}>
        <Button variant="outlined-sm" sx={styles.seeFullDetailsButton} onClick={handleSeeFullDetails} >
        
          {t("seeFullDetails")}
        </Button>
      </Box> */}
    </Box>
  );
};

export default CandidateFitScore;

const styles = {
  fittingScoreTitle: {
    fontFamily: "Montserrat",
    color: "var(--Dark-Tertiary-tr20, #080808)",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "30px",
  },
  fittingScoreTitleContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  fittingScoreMatching: {
    fontFamily: "Montserrat",
    color: "var(--Gray-600, #475467)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  fittingScoreValue: {
    fontFamily: "Montserrat",
    color: "var(--Gray-900, #101828)",
    fontSize: "36px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "44px",
    letterSpacing: "-0.72px",
  },
  fittingScoreMatchingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "absolute",
    left: "50%",
    bottom: "4px",
    transform: "translate(-50%, 0)",
    zIndex: 20,
  },
  fittingScoreProgress: {
    transform: "rotate(-180deg) !important",
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 0,
    "& .MuiCircularProgress-circle": {
      strokeLinecap: "round",
      stroke: "#E4E7EC",
    },
  },
  fittingScoreProgressContainer: {
    position: "relative",
    width: "240px",
    height: "132px",
    margin: { xs: "0 auto", sm: "0" },
  },
  trackSlot: (fitScore: number) => ({
    transform: "rotate(-180deg) !important",
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 10,
    "& .MuiCircularProgress-circle": {
      strokeLinecap: "round",
      stroke: fitScore > 60 ? "#008B07" : fitScore > 30 ? "#F3BC4E" : "#FF4D4E",
    },
  }),
  fittingScoreContainer: {
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    overflow: "hidden",
    background: "#FFF",
  },
  fittingScoreInnerContainer: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
  },
  fitScoreMeter: {
    display: "flex",
    padding: "24px",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "24px",
  },
  fittingScoreJustificationContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "16px",
    padding: "24px",
  },
  fittingScoreJustificationTitle: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  fittingScoreJustificationValue: {
    fontFamily: "Montserrat",
    color: "var(--Dark-Mode---100, #1E2021)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
  },
  fittingScoreJustificationValueComingSoon: {
    fontFamily: "Montserrat",
    color: "var(--Gray-600, #475467)",
    fontSize: "12px",
    fontWeight: 600,
    fontStyle: "italic",
    lineHeight: "16px",
  },
  seeFullDetailsButtonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "24px",
  },
  seeFullDetailsButton: {
    color: "var(--Gray-700, #344054) !important",
    border: "1px solid var(--Gray-300, #D0D5DD) !important",
    background: "var(--Base-White, #FFF) !important",
  },
};
